import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { useTheme } from '../contexts/ThemeContext';

const FailureTrends = ({ trends }) => {
  const { theme, getThemeClasses } = useTheme();
  const themeClasses = getThemeClasses();

  // Theme-aware component colors
  const COMPONENT_COLORS = theme === 'light' ? {
    engine: '#dc2626',      // Darker red for light mode
    suspension: '#d97706',  // Darker amber for light mode
    brakes: '#059669',      // Darker green for light mode
    transmission: '#7c3aed' // Darker purple for light mode
  } : {
    engine: '#ef4444',      // Red
    suspension: '#f59e0b',  // Amber
    brakes: '#10b981',      // Green
    transmission: '#8b5cf6' // Purple
  };

  // Transform data for grouped bar chart
  const data = [];

  if (trends && trends.componentFailures) {
    for (let year = 1; year <= 10; year++) {
      const yearKey = `year${year}`;
      const yearData = trends.componentFailures[yearKey];

      if (yearData) {
        // Calculate actual year (2015 to 2025, where year 1 = 2015)
        const actualYear = 2014 + year;
        data.push({
          year: actualYear.toString(),
          engine: yearData.engine || 0,
          suspension: yearData.suspension || 0,
          brakes: yearData.brakes || 0,
          transmission: yearData.transmission || 0,
        });
      }
    }
  }

  // Custom tooltip with theme support
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      // Calculate total failures for this year
      const totalFailures = payload.reduce((sum, entry) => sum + entry.value, 0);
      
      const tooltipBg = theme === 'light' ? 'bg-white/95 border-2 border-gray-300 shadow-lg' : 'bg-gray-900/95 border-2 border-military-600';
      const textPrimary = theme === 'light' ? 'text-gray-900' : 'text-white';
      const textSecondary = theme === 'light' ? 'text-gray-600' : 'text-gray-300';
      const textMuted = theme === 'light' ? 'text-gray-500' : 'text-gray-400';
      const borderColor = theme === 'light' ? 'border-gray-200' : 'border-gray-700';

      return (
        <div className={`${tooltipBg} rounded-lg p-4 shadow-xl`}>
          <p className={`${textPrimary} font-bold text-sm mb-2`}>{label}</p>
          <div className="space-y-1">
            {payload.map((entry, index) => {
              const percentage = totalFailures > 0 ? ((entry.value / totalFailures) * 100).toFixed(1) : 0;
              return (
                <div key={index} className="flex items-center justify-between gap-4">
                  <div className="flex items-center gap-2">
                    <div
                      className="w-3 h-3 rounded"
                      style={{ backgroundColor: entry.color }}
                    />
                    <span className={`${textSecondary} text-xs capitalize`}>{entry.name}:</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`${textPrimary} font-semibold text-xs`}>{entry.value}</span>
                    <span className={`${textMuted} text-xs`}>({percentage}%)</span>
                  </div>
                </div>
              );
            })}
          </div>
          <div className={`border-t ${borderColor} mt-2 pt-2`}>
            <div className="flex items-center justify-between">
              <span className={`${textMuted} text-xs`}>Total:</span>
              <span className={`${textPrimary} font-bold text-xs`}>{totalFailures}</span>
            </div>
          </div>
        </div>
      );
    }
    return null;
  };

  // Theme-aware chart colors
  const gridColor = theme === 'light' ? '#d1d5db' : '#374151';
  const axisColor = theme === 'light' ? '#6b7280' : '#9ca3af';

  return (
    <div className={`${themeClasses.chartBackground} border ${themeClasses.border} rounded-xl p-3 sm:p-4 lg:p-6`}>
      <h3 className={`text-base sm:text-lg lg:text-xl font-bold ${themeClasses.textPrimary} mb-3 sm:mb-4`}>Component Failure Trends (10-Year Analysis)</h3>
      <ResponsiveContainer width="100%" height={300}>
        <BarChart
          data={data}
          margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
          <XAxis
            dataKey="year"
            stroke={axisColor}
            tick={{ fontSize: 12, fill: axisColor }}
          />
          <YAxis
            stroke={axisColor}
            tick={{ fontSize: 12, fill: axisColor }}
            label={{ value: 'Failure Count', angle: -90, position: 'insideLeft', style: { fill: axisColor } }}
          />
          <Tooltip content={<CustomTooltip />} />
          <Legend
            wrapperStyle={{ paddingTop: '20px', color: axisColor }}
            iconType="rect"
            formatter={(value) => (
              <span style={{ color: axisColor }} className="capitalize">{value}</span>
            )}
          />
          <Bar
            dataKey="engine"
            fill={COMPONENT_COLORS.engine}
            radius={[4, 4, 0, 0]}
            name="Engine"
          />
          <Bar
            dataKey="suspension"
            fill={COMPONENT_COLORS.suspension}
            radius={[4, 4, 0, 0]}
            name="Suspension"
          />
          <Bar
            dataKey="brakes"
            fill={COMPONENT_COLORS.brakes}
            radius={[4, 4, 0, 0]}
            name="Brakes"
          />
          <Bar
            dataKey="transmission"
            fill={COMPONENT_COLORS.transmission}
            radius={[4, 4, 0, 0]}
            name="Transmission"
          />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

export default FailureTrends;
